/* eslint-disable max-lines */
import { receiveNewKeyword } from 'actions/identifier';
import ToggleButton from 'components/ToggleButton';
import WarningMsg from 'components/WarningMsg';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getActiveLanguageMatcherId,
  getCompareMode,
  getLanguageMatcherTypes,
  getShouldDisableEdit,
  saveIdentifierRequestLoading,
  unsavedChanges,
} from 'selectors/identifier';
import { useSelector } from 'store';
import { languageMatcherNotificationMsgs } from 'utils/userNotificationMsgs';
import AddLanguageMatcherInput from '../AddLanguageMatcherInput';
import TermsInput from '../AddLanguageMatcherInput/TermsInput';
import ChooseCombinationType from '../ChooseCombinationType';
import DeleteMatcherButton from '../DeleteMatcherButton';
import LanguageMatcherHeader from '../LanguageMatcherHeader';

type LanguageMatcherType = {
  isSaved: boolean;
};

const TokenTypeForm: React.FC<LanguageMatcherType> = ({ isSaved }) => {
  const dispatch = useDispatch();

  const unsaved = useSelector(unsavedChanges);
  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const activeLanguageMatcherId = useSelector(getActiveLanguageMatcherId);
  const isLoadingSaveIdentifier = useSelector(saveIdentifierRequestLoading);
  const languageMatcherTypes = useSelector(getLanguageMatcherTypes);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);

  const tokenExcludedFields = [
    'ent_types',
    'is_exclusion',
    'team_uuids',
    'tenses',
    'terms',
    'not_team_uuids',
    'pos',
    'type',
    'status',
  ];

  useEffect(() => {
    if (activeLanguageMatcher && activeLanguageMatcher.isNew) {
      const input = document.getElementById('name');
      input?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLanguageMatcherId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleToggleClick = (field: string): void => {
    // @ts-ignore
    const value = !activeLanguageMatcher[field];

    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'token',
        data: {
          // @ts-ignore
          key: field,
          value,
        },
      })
    );

    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'token',
        data: {
          key: 'saved',
          value: false,
        },
      })
    );
  };

  if (Object.keys(languageMatcherTypes).length === 0) return null;

  return (
    <div className="flex flex-col pt-5 pl-10 pr-4 pb-6 text-body h-full">
      <div className="flex flex-row justify-end">
        <WarningMsg
          message={languageMatcherNotificationMsgs.unsavedChanges}
          showLoading={isLoadingSaveIdentifier}
          showWarning={unsaved}
          showSavedMsg={isSaved}
        />
      </div>

      <div className="-mt-4 flex flex-col gap-4 text-body h-full">
        <LanguageMatcherHeader />

        <div className="mt-6 flex flex-row items-center gap-3">
          <ToggleButton
            // @ts-ignore
            isOn={activeLanguageMatcher.is_exclusion as boolean}
            handleToggleClick={(): void => handleToggleClick('is_exclusion')}
            disabled={compareMode || shouldDisable}
          />
          <span>Exclude matches</span>
        </div>

        <div className="flex flex-col gap-1">
          <TermsInput
            languageMatcherId={activeLanguageMatcherId}
            type="token"
            // @ts-ignore
            form="terms"
            enableGenerate
            enableImport
          />
        </div>

        <div className="flex flex-row gap-5">
          <div className="w-1/2">
            <AddLanguageMatcherInput
              languageMatcherId={activeLanguageMatcherId}
              type="token"
              form="pos"
            />
          </div>
          <div className="w-1/4">
            <AddLanguageMatcherInput
              languageMatcherId={activeLanguageMatcherId}
              type="token"
              form="tenses"
            />
          </div>
          <div className="w-1/4">
            <AddLanguageMatcherInput
              languageMatcherId={activeLanguageMatcherId}
              type="token"
              form="ent_types"
            />
          </div>
        </div>
        <div className="flex flex-col gap-4 mt-6">
          <TermsInput
            languageMatcherId={activeLanguageMatcherId}
            type="token"
            // @ts-ignore
            form="team_uuids"
          />

          <TermsInput
            languageMatcherId={activeLanguageMatcherId}
            type="token"
            // @ts-ignore
            form="not_team_uuids"
            useDisabledColor
          />
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-3">
            <ToggleButton
              // @ts-ignore
              isOn={activeLanguageMatcher.is_negated as boolean}
              handleToggleClick={(): void => handleToggleClick('is_negated')}
              disabled={compareMode || shouldDisable}
            />
            <span>Token Negated</span>
          </div>
        </div>

        <div>
          <ChooseCombinationType
            activeLanguageMatcher={activeLanguageMatcher}
            excludedFields={tokenExcludedFields}
          />
        </div>

        <div className="mt-auto pt-6 flex flex-row justify-end">
          <DeleteMatcherButton
            text="Delete Matcher"
            setValue={(): void => {
              // moot
            }}
            setHighlightedLanguageMatcher={(): void => {
              // moot
            }}
            activeLanguageMatcher={activeLanguageMatcher}
          />
        </div>
      </div>
    </div>
  );
};

export default TokenTypeForm;
